import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ScanSearch, FileText, CheckCircle, XCircle, Sparkles, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { scanText } from '../utils/api';

const ScanPage = () => {
  const [input, setInput] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [highlightedText, setHighlightedText] = useState('');
  const { isPro } = useAuth();
  const { language } = useLanguage();

  const handleScan = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    
    try {
      const data = await scanText(input, language);
      setResults(data.results);
      setStats({ 
        found: data.found, 
        shown: data.shown,
        valid: data.valid, 
        invalid: data.invalid,
        limited: data.limited,
        isPro: data.is_pro
      });
      
      // Create highlighted text
      if (data.highlights && data.highlights.length > 0) {
        let highlighted = input;
        // Sort by position descending to replace from end first (preserves positions)
        const sorted = [...data.highlights].sort((a, b) => b.start - a.start);
        
        sorted.forEach(h => {
          const before = highlighted.slice(0, h.start);
          const match = highlighted.slice(h.start, h.end);
          const after = highlighted.slice(h.end);
          const colorClass = h.type === 'aanslagnummer' ? 'bg-purple-200 dark:bg-purple-900/50' : 'bg-accent/20';
          highlighted = before + `<mark class="${colorClass} px-1 rounded font-mono">${match}</mark>` + after;
        });
        
        setHighlightedText(highlighted);
      } else {
        setHighlightedText('');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exampleText = language === 'nl' 
    ? `Geachte heer/mevrouw,

Hierbij ontvangt u de naheffingsaanslag omzetbelasting.

Aanslagnummer: 036000012L0123110
Betalingskenmerk: 4865 5274 6150 1300
Bedrag: € 1.250,00

Gelieve dit bedrag binnen 14 dagen te voldoen onder vermelding van het betalingskenmerk.

Met vriendelijke groet,
Belastingdienst`
    : `Dear Sir/Madam,

Please find enclosed the VAT additional assessment.

Assessment number: 036000012L0123110
Payment reference: 4865 5274 6150 1300
Amount: € 1,250.00

Please pay this amount within 14 days quoting the payment reference.

Kind regards,
Tax Authority`;

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <ScanSearch className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {language === 'nl' ? 'Tekst Scannen' : 'Scan Text'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {language === 'nl' 
              ? 'Plak een e-mail, brief of document en vind automatisch alle betalingskenmerken en aanslagnummers.'
              : 'Paste an email, letter or document and automatically find all payment references and assessment numbers.'}
          </p>
        </motion.div>

        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <FileText className="w-4 h-4" />
                {language === 'nl' ? 'Plak hier uw tekst' : 'Paste your text here'}
              </div>
              <button
                onClick={() => setInput(exampleText)}
                className="text-xs text-accent hover:text-accent-hover"
              >
                {language === 'nl' ? 'Voorbeeld laden' : 'Load example'}
              </button>
            </div>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={language === 'nl' 
                ? 'Plak hier een e-mail, brief of ander document...'
                : 'Paste an email, letter or other document here...'}
              className="w-full h-64 p-4 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 resize-none focus:outline-none"
              data-testid="scan-input"
            />
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                {input.length} {language === 'nl' ? 'tekens' : 'characters'}
              </span>
              <button
                onClick={handleScan}
                disabled={loading || !input.trim()}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-accent text-white font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="scan-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {language === 'nl' ? 'Scannen' : 'Scan'}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Stats Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-900 dark:text-white">{stats?.found}</span> {language === 'nl' ? 'gevonden' : 'found'}
                </span>
                <span className="flex items-center gap-1 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-semibold">{stats?.valid}</span> {language === 'nl' ? 'geldig' : 'valid'}
                </span>
                {stats?.invalid > 0 && (
                  <span className="flex items-center gap-1 text-sm text-error">
                    <XCircle className="w-4 h-4" />
                    <span className="font-semibold">{stats?.invalid}</span> {language === 'nl' ? 'ongeldig' : 'invalid'}
                  </span>
                )}
              </div>
              
              {stats?.limited && !isPro && (
                <Link
                  to="/pricing"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  {language === 'nl' 
                    ? `${stats.found - stats.shown} meer met Pro`
                    : `${stats.found - stats.shown} more with Pro`}
                </Link>
              )}
            </div>

            {/* Highlighted Text Preview */}
            {highlightedText && (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                  <h3 className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Gemarkeerde tekst' : 'Highlighted text'}
                  </h3>
                </div>
                <div 
                  className="p-4 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: highlightedText }}
                />
              </div>
            )}

            {/* Results Table */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                <h3 className="font-medium text-slate-900 dark:text-white">
                  {language === 'nl' ? 'Gedecodeerde resultaten' : 'Decoded results'}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Kenmerk</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{language === 'nl' ? 'Belastingtype' : 'Tax Type'}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{language === 'nl' ? 'Jaar' : 'Year'}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{language === 'nl' ? 'Tijdvak' : 'Period'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {results.map((r, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          {r.valid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error/10 text-error text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Error
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-900 dark:text-white">
                          {r.text_position?.original || r.kenmerk}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            r.input_type === 'aanslagnummer'
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                              : 'bg-accent/10 text-accent'
                          }`}>
                            {r.input_type === 'aanslagnummer' 
                              ? (language === 'nl' ? 'Aanslag' : 'Assessment')
                              : (language === 'nl' ? 'Betaling' : 'Payment')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.belastingtype?.[language] || r.error || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.jaar || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.tijdvak || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Info Box */}
        {!results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
          >
            <h3 className="font-medium text-slate-900 dark:text-white mb-3">
              {language === 'nl' ? 'Wat wordt herkend?' : 'What is recognized?'}
            </h3>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2" />
                <span><strong>{language === 'nl' ? 'Betalingskenmerken' : 'Payment references'}:</strong> 16-{language === 'nl' ? 'cijferige nummers' : 'digit numbers'} (bijv. 4865 5274 6150 1300)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2" />
                <span><strong>{language === 'nl' ? 'Aanslagnummers' : 'Assessment numbers'}:</strong> {language === 'nl' ? 'Met letter in het midden' : 'With letter in the middle'} (bijv. 036000012L0123110)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2" />
                <span>{language === 'nl' ? 'Werkt met e-mails, brieven, PDF-tekst, bankafschriften, etc.' : 'Works with emails, letters, PDF text, bank statements, etc.'}</span>
              </li>
            </ul>
            {!isPro && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {language === 'nl' 
                    ? 'Gratis: max 3 resultaten per scan. Upgrade naar Pro voor onbeperkt scannen.'
                    : 'Free: max 3 results per scan. Upgrade to Pro for unlimited scanning.'}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ScanPage;
